import { useEffect, useState } from 'react'
import {
  Terminal, ArrowRight, Sparkles, Copy, Check, X, Key,
} from 'lucide-react'
import ModalPortal from './ModalPortal'
import ApiTokenControls from './ApiTokenControls'
import { trackSegmentEvent } from '../lib/segment'
// The np-* styling for these callouts + modals lives in StartQaForm.css (the home of that design
// system). Import it so the callouts are styled wherever they're used (the classic StartQaForm and
// the chat-based OnboardingChat both render these).
import './StartQaForm.css'

// The two onboarding callouts shared by the classic new-project form and the chat-based onboarding:
// "Want your coding agent to handle it?" (AgentCta) and "Want to QA your app for free?" (FreeQaCta).
// Each is self-contained (banner button + the modal it opens).

const AGENT_PROMPT = `Use Replay QA to test and fix this app.

Run:

npx --yes replayqa@latest skill

Follow the workflow it prints completely. Set up or reuse the appropriate QA project, keep any required local app or proxy processes running, monitor QA, fix every reported bug in this codebase, mark each bug fixed with the CLI.`

/* "Want your coding agent to handle it?" banner + the prompt modal it opens. */
export function AgentCta() {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" className="np-agentcta" onClick={() => setOpen(true)} data-testid="agent-cta" data-analytics-event="dashboard-agent-cta-open">
        <span className="np-agentcta__icon" aria-hidden>
          <Terminal size={17} strokeWidth={1.75} />
        </span>
        <span className="np-agentcta__txt">
          <span className="np-agentcta__title">Want your coding agent to handle it?</span>
        </span>
        <span className="np-agentcta__action">
          Get the prompt <ArrowRight size={15} strokeWidth={1.75} aria-hidden />
        </span>
      </button>
      {open && <AgentPromptModal onClose={() => setOpen(false)} />}
    </>
  )
}

function AgentPromptModal({ onClose }: { onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  // The approved CLI prompt is static — the CLI discovers the backend itself, so there is no origin to
  // interpolate (#515 removed the builder this call site used).
  const agentPrompt = AGENT_PROMPT

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  async function copy() {
    try {
      await navigator.clipboard.writeText(agentPrompt)
    } catch {
      /* clipboard unavailable */
    }
    trackSegmentEvent('Copied agent prompt')
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <ModalPortal>
    <div
      className="np-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="np-modal" role="dialog" aria-modal="true" aria-label="Start QA from your coding agent" data-testid="agent-panel">
        <div className="np-modal__head">
          <div className="np-modal__heading">
            <span className="np-modal__icon" aria-hidden>
              <Terminal size={18} strokeWidth={1.75} />
            </span>
            <div>
              <h2 className="np-modal__title">Start QA from your coding agent</h2>
              <p className="np-modal__lead">
                Paste this prompt into Claude Code, Cursor, Codex, or any coding assistant. It uses the
                Replay QA CLI to run the full QA and fix loop until everything is confirmed fixed.
              </p>
            </div>
          </div>
          <button type="button" className="np-modal__close" onClick={onClose} aria-label="Close" data-analytics-event="dashboard-agent-modal-close">
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <div className="np-modal__body">
          <div className="np-prompt">
            <div className="np-prompt__bar">
              <span className="np-prompt__label">
                <Sparkles size={13} strokeWidth={1.75} aria-hidden /> Agent prompt
              </span>
              {/* Icon-only here — the footer holds the primary labeled "Copy prompt" action, so a
                  second labeled button in the code bar would just read as a duplicate. */}
              <button
                type="button"
                className={`np-prompt__copy${copied ? ' np-prompt__copy--done' : ''}`}
                onClick={copy}
                data-testid="agent-copy-btn"
                data-analytics-event="dashboard-agent-prompt-copy"
                aria-label={copied ? 'Copied' : 'Copy prompt'}
                title={copied ? 'Copied' : 'Copy prompt'}
              >
                {copied ? <Check size={14} strokeWidth={1.75} aria-hidden /> : <Copy size={14} strokeWidth={1.75} aria-hidden />}
              </button>
            </div>
            {/* Shown in full: this prompt is a handful of lines, and the CTA that opens this modal
                promises the prompt — clamping it to ~4 lines behind an expander hid most of the
                thing the user came for. The modal body scrolls if the viewport is short. */}
            <div className="np-prompt__body">
              <pre className="np-prompt__text" data-testid="agent-prompt">
                {agentPrompt}
              </pre>
            </div>
          </div>

          <div className="np-agent-token" data-testid="agent-token">
            <div className="np-agent-token__head">
              <span className="np-agent-token__title">
                <Key size={14} strokeWidth={1.75} aria-hidden /> Your API tokens
              </span>
              <p className="np-agent-token__lead">
                The CLI can sign in interactively. For non-interactive or direct API access, generate
                a token here and give it to your agent when asked. It is shown only once.
              </p>
            </div>
            <ApiTokenControls />
          </div>
        </div>

        <div className="np-modal__foot">
          <button type="button" className="np-submit" onClick={copy} data-analytics-event="dashboard-agent-prompt-copy">
            {copied ? <Check size={15} strokeWidth={1.75} aria-hidden /> : <Copy size={15} strokeWidth={1.75} aria-hidden />}
            {copied ? 'Prompt copied' : 'Copy prompt'}
          </button>
        </div>
      </div>
    </div>
    </ModalPortal>
  )
}
